import useRolePermission from "entities/auth/hooks/useRolePermission";
import { FaqGroup } from "entities/faqs/types";
import React, { FC } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import DragNDropTable from "shared/components/DragNDropTable";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import { FAQS_PATH } from "shared/routes/paths";
import FaqGroupDetails from "./components/FaqGroupDetails";
import { FaqGroupPageProvider, useFaqGroupPage } from "./context";
import { getPagesColumns } from "./helpers";

const FaqGroupContainer = () => (
  <FaqGroupPageProvider>
    <FaqGroupPage />
  </FaqGroupPageProvider>
);

const FaqGroupPage: FC = () => {
  const {
    data,
    isLoadingFaqGroups,
    faqGroupDrawer,
    columnsParams,
    deleteModal,
    handleDeleteFaqGroup,
    mutateReorderFaqGroups,
    isLoadingDeleteFaqGroup,
    isLoadingReorderList,
  } = useFaqGroupPage();
  const navigate = useNavigate();

  const writeAccess = useRolePermission("faq:write");

  const columns = getPagesColumns(columnsParams);

  return (
    <FlexColumn>
      <PageHeading>
        {writeAccess.allowed && (
          <PrimaryButton
            title="Add new group"
            size="medium"
            onClick={() => faqGroupDrawer.openDrawer()}
          />
        )}
      </PageHeading>
      <DragNDropTable<FaqGroup>
        items={data || []}
        columns={columns}
        reorderDisabled={!writeAccess.allowed}
        isItemsFetched={!isLoadingFaqGroups}
        isItemsLoading={isLoadingFaqGroups || isLoadingReorderList}
        noItemsMessage="No faqs found"
        onItemsReorder={async (faqGroups) => {
          const formattedList = faqGroups.map((faqGroup, idx) => ({
            ...faqGroup,
            order: idx + 1,
          }));
          mutateReorderFaqGroups({
            faqGroups: formattedList,
          });
        }}
        onRowClick={(params) =>
          navigate(
            generatePath(FAQS_PATH, {
              id: params.id,
            }),
          )
        }
      />
      <FaqGroupDetails />
      <DeleteConfirmationModal
        title={`Delete “${deleteModal.modalDetails?.title || ""}” group`}
        content={
          "Are you sure you want to delete this group? If you delete this group all questions will be removed."
        }
        applyButtonTitle="Yes, delete"
        onClose={deleteModal.closeModal}
        open={deleteModal.isOpen}
        handleSuccess={handleDeleteFaqGroup}
        applyButtonLoading={isLoadingDeleteFaqGroup}
      />
    </FlexColumn>
  );
};

export default FaqGroupContainer;
